/**
 * useEffectiveRate - Hook for fetching the effective rate for a unit on a specific date
 *
 * Uses the get_effective_rate_for_date function to resolve the rate through the hierarchy:
 * Unit Override > Site > Group > Company > System Default
 */

import { useState, useEffect, useCallback } from 'react';
import type { EffectiveRateResponse } from '@/types/rates';
import { transformEffectiveRateResponse } from '@/types/rates';
import { authenticatedFetch } from '@/lib/api';
import { format } from 'date-fns';

const API_BASE = '/api/v1/admin/rate-assignments';

/**
 * Fallback rate used when API fails or returns no data.
 * Matches system default: California NEM 3.0 baseline rates.
 */
const FALLBACK_RATE: EffectiveRateResponse = {
  rateId: null,
  rateName: 'System Default',
  gridSellRate: 0.08,
  gridImportRate: 0.18,
  dieselCostPerGallon: 4.50,
  rateSource: 'fallback' as const,
  sourceName: 'System Default (Fallback)',
};

interface UseEffectiveRateOptions {
  /** Unit ID to fetch rate for */
  unitId: string | undefined;
  /** Date to fetch rate for (defaults to today) */
  date?: Date | string;
  /** Whether to auto-fetch on mount/change */
  autoFetch?: boolean;
  /** Polling interval in ms (0 = disabled) */
  pollingInterval?: number;
}

interface UseEffectiveRateReturn {
  /** The effective rate data */
  rate: EffectiveRateResponse | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch the rate */
  refetch: () => Promise<void>;
}

/**
 * Fetches and manages the effective rate for a unit
 */
export function useEffectiveRate({
  unitId,
  date,
  autoFetch = true,
  pollingInterval = 0,
}: UseEffectiveRateOptions): UseEffectiveRateReturn {
  const [rate, setRate] = useState<EffectiveRateResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Format date string
  const dateString = date
    ? typeof date === 'string'
      ? date
      : format(date, 'yyyy-MM-dd')
    : undefined;

  const fetchRate = useCallback(async () => {
    if (!unitId) {
      setRate(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (dateString) {
        params.set('date', dateString);
      }

      const url = `${API_BASE}/effective/${unitId}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await authenticatedFetch(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // The API returns { unitId, date, effectiveRate: {...} }
      if (data.effectiveRate) {
        // Check for error in the response
        if (data.effectiveRate.error) {
          setError(data.effectiveRate.error);
          setRate(null);
        } else {
          setRate(transformEffectiveRateResponse(data.effectiveRate));
        }
      } else {
        setRate(null);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch effective rate';
      setError(message);
      // Use fallback rate instead of null to ensure financial calculations work
      setRate(FALLBACK_RATE);
      console.warn('[useEffectiveRate] Using fallback rate due to error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [unitId, dateString]);

  // Auto-fetch on mount and when dependencies change
  useEffect(() => {
    if (autoFetch && unitId) {
      fetchRate();
    }
  }, [autoFetch, unitId, dateString, fetchRate]);

  // Polling
  useEffect(() => {
    if (pollingInterval > 0 && unitId) {
      const interval = setInterval(fetchRate, pollingInterval);
      return () => clearInterval(interval);
    }
  }, [pollingInterval, unitId, fetchRate]);

  return {
    rate,
    isLoading,
    error,
    refetch: fetchRate,
  };
}

/**
 * Batch version - fetches effective rates for multiple units at once
 */
export function useEffectiveRatesBatch({
  unitIds,
  date,
  autoFetch = true,
}: {
  unitIds: string[];
  date?: Date | string;
  autoFetch?: boolean;
}): {
  rates: Record<string, EffectiveRateResponse>;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [rates, setRates] = useState<Record<string, EffectiveRateResponse>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dateString = date
    ? typeof date === 'string'
      ? date
      : format(date, 'yyyy-MM-dd')
    : undefined;

  const fetchRates = useCallback(async () => {
    if (!unitIds || unitIds.length === 0) {
      setRates({});
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch rates in parallel (batch of individual requests)
      // In the future, we could add a batch API endpoint
      const results = await Promise.all(
        unitIds.map(async unitId => {
          const params = new URLSearchParams();
          if (dateString) {
            params.set('date', dateString);
          }

          const url = `${API_BASE}/effective/${unitId}${params.toString() ? `?${params.toString()}` : ''}`;
          const response = await authenticatedFetch(url);

          if (!response.ok) {
            throw new Error(`Failed to fetch rate for unit ${unitId}`);
          }

          const data = await response.json();
          return { unitId, rate: data.effectiveRate };
        })
      );

      const ratesMap: Record<string, EffectiveRateResponse> = {};
      for (const { unitId, rate: rateData } of results) {
        if (rateData && !rateData.error) {
          ratesMap[unitId] = transformEffectiveRateResponse(rateData);
        }
      }

      setRates(ratesMap);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch effective rates';
      setError(message);
      console.error('[useEffectiveRatesBatch] Error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [unitIds, dateString]);

  useEffect(() => {
    if (autoFetch && unitIds.length > 0) {
      fetchRates();
    }
  }, [autoFetch, unitIds.join(','), dateString]);

  return {
    rates,
    isLoading,
    error,
    refetch: fetchRates,
  };
}

/**
 * Group effective rate response (matches API response)
 */
export interface GroupEffectiveRateResponse {
  rateId: string | null;
  rateName: string;
  gridImportRate: number;
  gridSellRate: number;
  dieselCostPerGallon: number | null;
  utilityProvider: string | null;
  nemVersion: string | null;
  rateSource: 'group' | 'company' | 'system' | 'fallback';
  sourceName: string | null;
  sourceId: string | null;
}

interface UseGroupEffectiveRateOptions {
  /** Group ID to fetch rate for */
  groupId: string | undefined;
  /** Whether to auto-fetch on mount/change */
  autoFetch?: boolean;
}

interface UseGroupEffectiveRateReturn {
  /** The effective rate data for the group */
  rate: GroupEffectiveRateResponse | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch the rate */
  refetch: () => Promise<void>;
}

/**
 * Fetches and manages the effective rate for a group
 * Hierarchy: Group default → Company default → System default
 */
export function useGroupEffectiveRate({
  groupId,
  autoFetch = true,
}: UseGroupEffectiveRateOptions): UseGroupEffectiveRateReturn {
  const [rate, setRate] = useState<GroupEffectiveRateResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRate = useCallback(async () => {
    if (!groupId) {
      setRate(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const url = `${API_BASE}/effective/group/${groupId}`;
      const response = await authenticatedFetch(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.effectiveRate) {
        setRate(data.effectiveRate);
      } else {
        setRate(null);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch group effective rate';
      setError(message);
      setRate(null);
      console.error('[useGroupEffectiveRate] Error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [groupId]);

  // Auto-fetch on mount and when dependencies change
  useEffect(() => {
    if (autoFetch && groupId) {
      fetchRate();
    }
  }, [autoFetch, groupId, fetchRate]);

  return {
    rate,
    isLoading,
    error,
    refetch: fetchRate,
  };
}

/**
 * Fetches effective rates for multiple groups
 * Returns rates keyed by group ID
 */
export function useGroupEffectiveRatesBatch({
  groupIds,
  autoFetch = true,
}: {
  groupIds: string[];
  autoFetch?: boolean;
}): {
  rates: Record<string, GroupEffectiveRateResponse>;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [rates, setRates] = useState<Record<string, GroupEffectiveRateResponse>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRates = useCallback(async () => {
    if (!groupIds || groupIds.length === 0) {
      setRates({});
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch rates in parallel
      const results = await Promise.all(
        groupIds.map(async groupId => {
          const url = `${API_BASE}/effective/group/${groupId}`;
          const response = await authenticatedFetch(url);

          if (!response.ok) {
            // Don't fail the whole batch for one error
            console.warn(`Failed to fetch rate for group ${groupId}`);
            return { groupId, rate: null };
          }

          const data = await response.json();
          return { groupId, rate: data.effectiveRate };
        })
      );

      const ratesMap: Record<string, GroupEffectiveRateResponse> = {};
      for (const { groupId, rate: rateData } of results) {
        if (rateData) {
          ratesMap[groupId] = rateData;
        }
      }

      setRates(ratesMap);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch group effective rates';
      setError(message);
      console.error('[useGroupEffectiveRatesBatch] Error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [groupIds.join(',')]);

  useEffect(() => {
    if (autoFetch && groupIds.length > 0) {
      fetchRates();
    }
  }, [autoFetch, groupIds.join(',')]);

  return {
    rates,
    isLoading,
    error,
    refetch: fetchRates,
  };
}
